import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H4,
  H5,
  H6,
  InputText,
} from "@sellar-io/components";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { AdminOnly } from "../../../components/authFilter/AdminOnly";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { Switch } from "@sellar-io/components";
import {
  featureFlags,
  FeatureFlagKey,
  useFeatureFlagStore,
} from "../../../hooks/useFeatureFlags";
import { RemoteFeatureFlagCard } from "./RemoteFeatureFlagCard";
import { Icon } from "../../../components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { useBusinessUserList } from "../../../hooks/api/v2/businessUser/useBusinessUserList";
import { useSellarUser } from "../../../hooks";
import { useHubspotSyncBuyer } from "../../../hooks/api/v2/hubspot/useHubspotSyncBuyer";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { BUSINESS } from "../../../constants/routes";
import { useDeleteBuyerCreditCheck } from "../../../hooks/api/v2/retailer/useDeleteBuyerCreditCheck";
import { useHubspotBackwardsSync } from "../../../hooks/api/v2/hubspot/useHubspotBackwardsSync";

export const FeatureFlagsSettings = (): ReactElement => {
  const { isTwoPanel } = useDashboardContext();
  const navigate = useNavigate();
  return (
    <AdminOnly>
      <NavigationBar
        title={"Feature Flags"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={isTwoPanel ? undefined : () => navigate(BUSINESS)}
      />
      <div className={"m-2 flex flex-col gap-2"}>
        <LocalFeatureFlagCard />
        <RemoteFeatureFlagCard />
        <HubspotSyncCard />
        <HubspotBackwardsSyncCard />
        <ClearSoftCreditChecksCard />
      </div>
    </AdminOnly>
  );
};

const LocalFeatureFlagCard = () => {
  const store = useFeatureFlagStore();

  return (
    <Card className={twMerge("bg-white", "m-auto w-full max-w-[900px]")}>
      <CardHeader className={twMerge("bg-white", "p-4")}>
        <H4>Feature Flags</H4>
        <H6>These settings can be enabled to preview early access features.</H6>
      </CardHeader>
      <CardContent className={"flex flex-col gap-4 p-4"}>
        {Object.keys(featureFlags).map((keyString) => {
          const flagKey = keyString as FeatureFlagKey;
          const flagObject = featureFlags[flagKey];

          return (
            <div key={flagKey} className={"flex items-center justify-between"}>
              <div>
                <H5>{flagObject.name}</H5>
                <H6>{flagObject.description}</H6>
              </div>
              <Switch
                onChange={() => store.toggleFlag(flagKey)}
                checked={store.isActive(flagKey)}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const HubspotSyncCard = () => {
  const businessUserListQuery = useBusinessUserList({});
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();

  const [active, setActive] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const userId =
    selectedUserId ?? businessUserListQuery.data?.pages[0]?.[0]?.User.id;

  const hubspotSyncBuyerMutation = useHubspotSyncBuyer();

  const onSubmit = () => {
    hubspotSyncBuyerMutation.mutate(
      {
        userId: userId ?? 0,
        businessId: business?.id ?? 0,
      },
      {
        onSuccess: () => {
          setActive(false);
        },
      },
    );
  };

  if (business?.type === "supplier") return;

  return (
    <>
      <Card className={twMerge("bg-white", "m-auto w-full max-w-[900px]")}>
        <CardHeader className={twMerge("bg-white", "p-4")}>
          <H4>Sync buyer details to Hubspot</H4>
          <H6>Manually sync user, and company details up to Hubspot.</H6>
        </CardHeader>
        <CardContent className={"flex flex-col gap-4 p-4"}>
          <Button
            className="flex items-center gap-2"
            onClick={() => setActive(true)}
          >
            Sync
            <Icon icon="SYNC" />
          </Button>
        </CardContent>
      </Card>

      <Dialog active={active} onDismiss={() => setActive(false)}>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => setActive(false)}
          className={"relative z-10 pb-0"}
          title="Sync to Hubspot"
        />
        <DialogContent className={"flex flex-col gap-4"}>
          <H5>{business?.name}</H5>
          <hr className="border-t-zinc-300" />
          <div className="flex flex-col gap-2">
            <span className="mr-auto inline-block text-sm">Select contact</span>
            <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
              <InputSelect
                value={userId}
                onChange={(e) => {
                  setSelectedUserId(Number.parseInt(e.target.value));
                }}
                className="text-sm font-medium"
              >
                {(businessUserListQuery.data?.pages[0]?.length === 0 ||
                  !userId) &&
                user?.type !== "admin" ? (
                  <InputOption>No users.</InputOption>
                ) : (
                  businessUserListQuery.data?.pages.map((page) =>
                    page.map((businessUser) => (
                      <InputOption
                        value={businessUser.User.id}
                        key={businessUser.User.id}
                      >
                        {businessUser.User.email} - {businessUser.User.name}
                      </InputOption>
                    )),
                  )
                )}
              </InputSelect>
            </InputContainer>
          </div>
        </DialogContent>
        <DialogCTA>
          <Button
            loading={hubspotSyncBuyerMutation.isPending}
            className="flex items-center gap-2"
            onClick={onSubmit}
          >
            Sync now
            <Icon icon="SYNC" />
          </Button>
        </DialogCTA>
      </Dialog>
    </>
  );
};

const HubspotBackwardsSyncCard = () => {
  const businessUserListQuery = useBusinessUserList({});
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();

  const [active, setActive] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const userId =
    selectedUserId ?? businessUserListQuery.data?.pages[0]?.[0]?.User.id;
  const [hubspotContactId, setHubspotContactId] = useState<string>();
  const [hubspotCompanyId, setHubspotCompanyId] = useState<string>();

  const hubspotBackwardsSyncMutation = useHubspotBackwardsSync();

  const onSubmit = () => {
    hubspotBackwardsSyncMutation.mutate(
      {
        userId: userId ?? 0,
        businessId: business?.id ?? 0,
        hubspotContactId,
        hubspotCompanyId,
      },
      {
        onSuccess: () => {
          setActive(false);
        },
      },
    );
  };

  if (business?.type === "supplier") return;

  return (
    <>
      <Card className={twMerge("bg-white", "m-auto w-full max-w-[900px]")}>
        <CardHeader className={twMerge("bg-white", "p-4")}>
          <H4>Add hubspot details for a user or company</H4>
          <H6>Manually sync hubspot IDs to a user or company</H6>
        </CardHeader>
        <CardContent className={"flex flex-col gap-4 p-4"}>
          <Button
            className="flex items-center gap-2"
            onClick={() => setActive(true)}
          >
            Sync
            <Icon icon="SYNC" />
          </Button>
        </CardContent>
      </Card>

      <Dialog active={active} onDismiss={() => setActive(false)}>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => setActive(false)}
          className={"relative z-10 pb-0"}
          title="Sync from Hubspot"
        />
        <DialogContent className={"flex flex-col gap-4"}>
          <H5>{business?.name}</H5>
          <hr className="border-t-zinc-300" />
          {!business?.hubspotCompanyId && (
            <>
              <div className="flex flex-col gap-2">
                <span className="mr-auto inline-block text-sm">
                  Company hubspot Id
                </span>
                <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
                  <InputText
                    value={hubspotCompanyId}
                    onChange={(e) => {
                      setHubspotCompanyId(e.target.value);
                    }}
                    className="text-sm font-medium"
                  />
                </InputContainer>
              </div>
              <div className="w-full text-center font-semibold">OR</div>
            </>
          )}
          <div className="flex flex-col gap-2">
            <span className="mr-auto inline-block text-sm">Select contact</span>
            <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
              <InputSelect
                value={userId}
                onChange={(e) => {
                  setSelectedUserId(Number.parseInt(e.target.value));
                }}
                className="text-sm font-medium"
              >
                {(businessUserListQuery.data?.pages[0]?.length === 0 ||
                  !userId) &&
                user?.type !== "admin" ? (
                  <InputOption>No users.</InputOption>
                ) : (
                  businessUserListQuery.data?.pages.map((page) =>
                    page
                      .filter((bu) => !bu.User.hubspotId)
                      .map((businessUser) => (
                        <InputOption
                          value={businessUser.User.id}
                          key={businessUser.User.id}
                        >
                          {businessUser.User.email} - {businessUser.User.name}
                        </InputOption>
                      )),
                  )
                )}
              </InputSelect>
            </InputContainer>
            <span className="mr-auto inline-block text-sm">
              Contact hubspot Id
            </span>
            <InputContainer className="py-2 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
              <InputText
                value={hubspotContactId}
                onChange={(e) => {
                  setHubspotContactId(e.target.value);
                }}
                className="text-sm font-medium"
              />
            </InputContainer>
          </div>
        </DialogContent>
        <DialogCTA>
          <Button
            loading={hubspotBackwardsSyncMutation.isPending}
            className="flex items-center gap-2"
            onClick={onSubmit}
          >
            Sync now
            <Icon icon="SYNC" />
          </Button>
        </DialogCTA>
      </Dialog>
    </>
  );
};

const ClearSoftCreditChecksCard = () => {
  const { business } = useSellarCurrentBusiness();
  const [active, setActive] = useState(false);

  const deleteBuyerCreditCheck = useDeleteBuyerCreditCheck(
    business?.Retailer?.id ?? 0,
  );

  const onSubmit = () => {
    deleteBuyerCreditCheck.mutate(undefined, {
      onSuccess: () => {
        setActive(false);
      },
      onError: () => {
        console.log("on error");
      },
    });
  };

  if (business?.type === "supplier") return;

  return (
    <>
      <Card className={twMerge("bg-white", "m-auto w-full max-w-[900px]")}>
        <CardHeader className={twMerge("bg-white", "p-4")}>
          <H4>Clear all buyer previous soft credit checks</H4>
          <H6>
            Delete's all previous credit checks for every user that has made a
            credit check against this buyer.
          </H6>
        </CardHeader>
        <CardContent className={"flex flex-col gap-4 p-4"}>
          <Button
            className="flex items-center gap-2"
            onClick={() => setActive(true)}
          >
            Clear all credit checks
            <Icon icon="DELETE" />
          </Button>
        </CardContent>
      </Card>

      <Dialog active={active} onDismiss={() => setActive(false)}>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => setActive(false)}
          className={"relative z-10 pb-0"}
          title="Clear all buyer previous soft credit checks"
        />
        <DialogContent className={"flex flex-col gap-4"}>
          <div className="text-center">
            Confirm the removal of all previous credit checks made against{" "}
            {business?.name}
          </div>
        </DialogContent>
        <DialogCTA>
          <Button
            loading={deleteBuyerCreditCheck.isPending}
            className="flex items-center gap-2"
            onClick={onSubmit}
          >
            Confirm
            <Icon icon="DELETE" />
          </Button>
        </DialogCTA>
      </Dialog>
    </>
  );
};
